const IMAGE_MIME = /^image\//i;
const IMAGE_EXT = /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i;

export const PHOTO_MAX_EDGE = 800;
export const PHOTO_QUALITY = 75;
export const PHOTO_MAX_BYTES = 350_000;

export const DOCUMENT_MAX_EDGE = 1280;
export const DOCUMENT_QUALITY = 70;
export const DOCUMENT_MAX_BYTES = 500_000;

export function isImageFile(file: File): boolean {
  return IMAGE_MIME.test(file.type) || IMAGE_EXT.test(file.name);
}

function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Impossible de lire l'image"));
    };
    image.src = url;
  });
}

function canvasToJpegFile(
  canvas: HTMLCanvasElement,
  quality: number,
  fileName: string,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Compression image échouée"));
          return;
        }
        const baseName = fileName.replace(/\.[^.]+$/, "") || "image";
        resolve(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      quality,
    );
  });
}

export async function resizeImageToFile(
  file: File,
  maxWidth = PHOTO_MAX_EDGE,
  maxHeight = PHOTO_MAX_EDGE,
  qualityPercent = PHOTO_QUALITY,
  maxBytes = PHOTO_MAX_BYTES,
): Promise<File> {
  const image = await loadImageElement(file);
  const scale = Math.min(1, maxWidth / image.width, maxHeight / image.height);
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas indisponible");
  }

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, 0, 0, width, height);

  let quality = Math.min(1, Math.max(0.4, qualityPercent / 100));
  let result = await canvasToJpegFile(canvas, quality, file.name);

  while (result.size > maxBytes && quality > 0.45) {
    quality = Math.max(0.45, quality - 0.1);
    result = await canvasToJpegFile(canvas, quality, file.name);
  }

  return result;
}

export async function compressImageIfNeeded(
  file: File,
  maxWidth: number,
  maxHeight: number,
  qualityPercent: number,
  maxBytes: number,
): Promise<File> {
  if (!isImageFile(file)) {
    return file;
  }

  if (file.type === "image/jpeg" && file.size <= maxBytes) {
    return file;
  }

  return resizeImageToFile(file, maxWidth, maxHeight, qualityPercent, maxBytes);
}
